/**
 * Where to put a walkthrough callout so it does not cover what the therapist
 * needs to see.
 *
 * A modal is the hard case: drawn over it, the callout hides the very controls
 * the step is pointing at. So when one is open the callout is docked in the
 * clear space beside it — whichever side has room — and only falls back to
 * sitting near the highlight when the modal spans the width.
 */

// Wide enough for a sentence of Hebrew at reading size. Narrower, the
// instruction broke into four or five ragged lines.
export const CALLOUT_WIDTH = 330;
export const CALLOUT_HEIGHT = 180;
const GAP = 16;

export interface Box {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export const calloutPosition = (
  target: Box | null,
  overlay: Box | null,
  viewport: Viewport
): { top: number; left: number } => {
  const maxLeft = viewport.width - CALLOUT_WIDTH - 12;
  const maxTop = viewport.height - CALLOUT_HEIGHT - 12;
  const clampLeft = (x: number) => Math.min(Math.max(12, x), Math.max(12, maxLeft));
  const clampTop = (y: number) => Math.min(Math.max(12, y), Math.max(12, maxTop));

  if (overlay) {
    const top = clampTop(target ? target.top - 20 : overlay.top);
    if (viewport.width - overlay.right >= CALLOUT_WIDTH + GAP) {
      return { top, left: clampLeft(overlay.right + GAP) };
    }
    if (overlay.left >= CALLOUT_WIDTH + GAP) {
      return { top, left: clampLeft(overlay.left - CALLOUT_WIDTH - GAP) };
    }
    // The modal spans the width: sit above or below it rather than over it.
    const below = overlay.bottom + GAP;
    if (below + CALLOUT_HEIGHT <= viewport.height) {
      return { top: clampTop(below), left: clampLeft(overlay.left) };
    }
    return { top: clampTop(overlay.top - CALLOUT_HEIGHT - GAP), left: clampLeft(overlay.left) };
  }

  if (!target) {
    return {
      top: clampTop(viewport.height / 2 - 90),
      left: clampLeft(viewport.width / 2 - CALLOUT_WIDTH / 2),
    };
  }

  const below = target.bottom + 14;
  return {
    top: clampTop(
      below + CALLOUT_HEIGHT > viewport.height ? target.top - CALLOUT_HEIGHT - 14 : below
    ),
    left: clampLeft(target.left - 20),
  };
};
